import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import documentCollectionService from '@/services/document-collection.service';

export const documentCollectionKeys = {
  all: ['document-collections'] as const,
  list: (filters?: Record<string, string>) => [...documentCollectionKeys.all, 'list', filters] as const,
  detail: (id: string) => [...documentCollectionKeys.all, 'detail', id] as const,
  package: (deliveryNoteId: string) => [...documentCollectionKeys.all, 'package', deliveryNoteId] as const,
};

export function useDocumentCollections(filters?: Record<string, string>) {
  return useQuery({
    queryKey: documentCollectionKeys.list(filters),
    queryFn: () => documentCollectionService.list(filters),
  });
}

export function useDocumentCollection(id: string | undefined) {
  return useQuery({
    queryKey: documentCollectionKeys.detail(id!),
    queryFn: () => documentCollectionService.getById(id!),
    enabled: !!id,
  });
}

export interface DeliveryPackageItem {
  id: string;
  category: string;
  document?: { id: string; document_name?: string; file_name?: string };
}

export function useDeliveryDocumentPackage(deliveryNoteId: string | undefined) {
  return useQuery({
    queryKey: documentCollectionKeys.package(deliveryNoteId!),
    queryFn: () => documentCollectionService.getPackageByDelivery(deliveryNoteId!) as Promise<DeliveryPackageItem[]>,
    enabled: !!deliveryNoteId,
  });
}

export function useRecollectDocuments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => documentCollectionService.recollect(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: documentCollectionKeys.detail(id) });
    },
  });
}

export function useSyncCollectionUploads(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => documentCollectionService.syncUploads(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: documentCollectionKeys.detail(id) }),
  });
}

export function useBulkUpdateCollectionItems(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { included: boolean; all?: boolean; item_ids?: string[] }) =>
      documentCollectionService.bulkUpdateItems(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: documentCollectionKeys.detail(id) }),
  });
}

export function useCreateDocumentCollectionForDelivery(deliveryNoteId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => documentCollectionService.createForDelivery(deliveryNoteId!),
    onSuccess: (dcr) => {
      qc.invalidateQueries({ queryKey: ['deliveries', 'detail', deliveryNoteId] });
      qc.invalidateQueries({ queryKey: documentCollectionKeys.detail(dcr.id) });
    },
  });
}

export function useUpdateCollectionItem(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      body,
    }: {
      itemId: string;
      body: { included?: boolean; category?: string; remarks?: string };
    }) => documentCollectionService.updateItem(id, itemId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: documentCollectionKeys.detail(id) }),
  });
}

export function useUpdateCollectionChecklist(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      checklistId,
      body,
    }: {
      checklistId: string;
      body: { status: string; remarks?: string };
    }) => documentCollectionService.updateChecklist(id, checklistId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: documentCollectionKeys.detail(id) }),
  });
}

export function useSubmitDocumentCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => documentCollectionService.submitForApproval(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: documentCollectionKeys.detail(id) });
      qc.invalidateQueries({ queryKey: documentCollectionKeys.all });
    },
  });
}
